import { useEncounter } from '@/contexts/encounter-context';
import { PageContainer } from '@/components/containers/page-container';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { useMediaQuery } from '@/hooks/use-media-query';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { useState } from 'react';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { Section } from '@/components/containers/section';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { MedicationsForm } from '../../../components/encounter/medical-history/forms/medications-form';
import { MedicalHistory, UpdateMedicalHistoryRequest } from '@/api/api.schemas';
import { AllergiesForm } from '../../../components/encounter/medical-history/forms/allergies-form';
import styles from './medical-history-page.module.css';
import { SurgeriesForm } from '../../../components/encounter/medical-history/forms/surgeries-form';
import { FamilyDiseasesForm } from '../../../components/encounter/medical-history/forms/family-diseases-form';
import { IllnessesForm } from '../../../components/encounter/medical-history/forms/illnesses-form';
import { useUpdateEncountersMedicalHistory } from '@/api/update-medical-history';
import { useAsyncAction } from '@/hooks/use-async-action';
import { Button } from '@/components/button';
import { useMedicalHistoryState } from '@/hooks/use-medical-history-state';
import { useTranslation } from '@/contexts/i18n-context';
import { usePostSubmitNavigation } from '@/hooks/use-post-submit-navigation';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';
import { useGetEncountersMedicalHistory } from '@/api/get-medical-history';
import { Loader } from '@/components/loaders/loader';

type FormSections = 'medications' | 'allergies' | 'surgeries' | 'family-diseases' | 'illnesses';

const translationBase = 'visit.medicalHistory';

export function MedicalHistoryPage() {
  const { encounter } = useEncounter();
  const { data: medicalHistory, isFetching } = useGetEncountersMedicalHistory(encounter.id);

  if (isFetching) {
    return <Loader type="large" />;
  }

  return <MedicalHistoryForm medicalHistory={medicalHistory} />;
}

interface MedicalHistoryFormProps {
  medicalHistory?: MedicalHistory;
}

function MedicalHistoryForm({ medicalHistory }: MedicalHistoryFormProps) {
  const { encounter, invalidateEncounter } = useEncounter();
  const { mutateAsync } = useUpdateEncountersMedicalHistory();
  const [skipButtonClicked, setSkipButtonClicked] = useState(false);
  const [openedSection, setOpenedSection] = useState<FormSections | null>(null);
  const { t } = useTranslation();
  const { navigatePostSubmit } = usePostSubmitNavigation();
  const { editMode } = useVisitNavigation();

  const historyState = useMedicalHistoryState(medicalHistory);

  const showSkipButton =
    !editMode &&
    !historyState.medications.length &&
    !historyState.allergies.length &&
    !historyState.surgeries.length &&
    !historyState.familyDiseases.length &&
    !historyState.illnesses.length;

  const { trigger: updateMedicalHistoryAsync, loading: isUpdatingMedicalHistory } = useAsyncAction(
    async (data: UpdateMedicalHistoryRequest, isSkip: boolean = false) => {
      setSkipButtonClicked(isSkip);

      await mutateAsync({
        encounterId: encounter.id,
        data,
      });
      await invalidateEncounter();

      navigatePostSubmit({ isIncomplete: data.incomplete });
    },
  );

  const isMobile = useMediaQuery('--screen-md');

  async function handleNext(isIncomplete: boolean, isSkip: boolean = false) {
    updateMedicalHistoryAsync(
      {
        medications: historyState.medications,
        allergies: historyState.allergies,
        surgeries: historyState.surgeries.map((s) => s.text),
        familyDiseases: historyState.familyDiseases.map((d) => d.text),
        illnesses: historyState.illnesses.map((i) => ({
          text: i.text,
          type: i.type,
        })),
        incomplete: isIncomplete,
      },
      isSkip,
    );
  }

  const handleSectionClick = (section: FormSections) => {
    setOpenedSection((prev) => (prev === section ? null : section));
  };

  return (
    <PageContainer>
      {!isMobile && (
        <VisitTitleButtons
          saveProgress={{ onSaveClick: () => handleNext(true), showModal: historyState.isDirty }}
        />
      )}
      <GradientFormTitle>
        {isMobile && <VisitTitleButtons />}
        <VisitNavigation />
        <h1 className="h3">{t(`${translationBase}.title`)}</h1>
        <p>{t(`${translationBase}.description`)}</p>
        {isMobile && (
          <SaveProgressButton
            onSaveClick={() => handleNext(true)}
            showModal={historyState.isDirty}
          />
        )}
      </GradientFormTitle>
      <Section className={styles.mainContainer}>
        <h2 className="h4">{t(`${translationBase}.selectAnyThatApplyToYou`)}</h2>
        <MedicationsForm
          onRemove={historyState.removeMedication}
          onAdd={historyState.addMedication}
          medications={historyState.medications}
          isOpen={openedSection === 'medications'}
          onExpanderClick={() => handleSectionClick('medications')}
        />
        <AllergiesForm
          onRemove={historyState.removeAllergy}
          onAdd={historyState.addAllergy}
          allergies={historyState.allergies}
          isOpen={openedSection === 'allergies'}
          onExpanderClick={() => handleSectionClick('allergies')}
        />
        <SurgeriesForm
          onRemove={historyState.removeSurgery}
          onAdd={historyState.addSurgery}
          surgeries={historyState.surgeries}
          isOpen={openedSection === 'surgeries'}
          onExpanderClick={() => handleSectionClick('surgeries')}
        />
        <FamilyDiseasesForm
          onRemove={historyState.removeFamilyDisease}
          onAdd={historyState.addFamilyDisease}
          familyDiseases={historyState.familyDiseases}
          isOpen={openedSection === 'family-diseases'}
          onExpanderClick={() => handleSectionClick('family-diseases')}
        />
        <IllnessesForm
          onRemove={historyState.removeIllness}
          onAdd={historyState.addIllness}
          illnesses={historyState.illnesses}
          isOpen={openedSection === 'illnesses'}
          onExpanderClick={() => handleSectionClick('illnesses')}
        />
      </Section>
      <VisitFormButtonRow
        nextStep={t(`${translationBase}.nextStep`)}
        buttonTextKey="common.next"
        type="button"
        onClick={() => handleNext(false)}
        rightIcon="arrow_forward"
        loading={!skipButtonClicked && isUpdatingMedicalHistory}
        disabled={isUpdatingMedicalHistory}
        secondaryButton={
          showSkipButton ? (
            <Button
              className={styles.skipButton}
              disabled={isUpdatingMedicalHistory}
              loading={skipButtonClicked && isUpdatingMedicalHistory}
              variant="outlined"
              color="accent"
              onClick={() => handleNext(false, true)}
            >
              {t(`${translationBase}.skipButton`)}
            </Button>
          ) : undefined
        }
      />
    </PageContainer>
  );
}
